import styled, { DefaultTheme } from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";

export type V3CalloutTone = "info" | "success" | "warning" | "error";

const BACKGROUND: Record<V3CalloutTone, (theme: DefaultTheme) => string> = {
  info: (theme) => theme.colors.ui.background.info,
  // Tinted, not the solid banner green: these sit inside cards, not across them.
  success: (theme) =>
    theme.colors.domainSpecific.badgeTypes.successSubtle.background,
  warning: (theme) => theme.colors.ui.background.warning,
  error: (theme) => theme.colors.ui.background.error,
};

const ACCENT: Record<V3CalloutTone, (theme: DefaultTheme) => string> = {
  info: (theme) => theme.colors.ui.text.info,
  success: (theme) => theme.colors.ui.text.success,
  warning: (theme) => theme.colors.ui.text.warning,
  error: (theme) => theme.colors.ui.text.error,
};

/** Figma info-callout fills on the dark palette. */
const DARK_BACKGROUND: Record<V3CalloutTone, (theme: DefaultTheme) => string> =
  {
    info: (theme) => theme.colors.domainSpecific.callout.neutral.background,
    success: (theme) => theme.colors.domainSpecific.callout.success.background,
    warning: (theme) => theme.colors.domainSpecific.callout.warning.background,
    error: (theme) => theme.colors.domainSpecific.callout.error.background,
  };

/** Figma background/* fills, the ones the mobile design-system callout uses. */
const SOLID_BACKGROUND: Record<V3CalloutTone, (theme: DefaultTheme) => string> =
  {
    ...BACKGROUND,
    info: (theme) => theme.colors.ui.background.info,
    error: (theme) => theme.colors.ui.background.error,
  };

const LINK: Record<V3CalloutTone, (theme: DefaultTheme) => string> = {
  info: (theme) => theme.colors.ui.text.info,
  success: (theme) => theme.colors.ui.text.info,
  warning: (theme) => theme.colors.ui.text.info,
  error: (theme) => theme.colors.ui.text.error,
};

export const Container = styled.div<{ $tone: V3CalloutTone; $solid: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm}px;
  padding: ${({ theme }) => theme.space.md}px
    ${({ theme }) => theme.space.card.inset}px;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  background: ${({ theme, $tone, $solid }) =>
    ($solid
      ? SOLID_BACKGROUND
      : theme.mode === "dark"
      ? DARK_BACKGROUND
      : BACKGROUND)[$tone](theme)};
`;

export const IconSlot = styled.span<{ $tone: V3CalloutTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ theme }) => theme.size.icon.sm}px;
  height: ${({ theme }) => theme.size.icon.sm}px;
  color: ${({ theme, $tone }) => ACCENT[$tone](theme)};
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["3xs"]}px;
  min-width: 0;
`;

export const Title = styled.span<{ $tone: V3CalloutTone }>`
  ${typographyCSS("label.sm-semibold")}
  letter-spacing: -0.1px;
  color: ${({ theme, $tone }) => ACCENT[$tone](theme)};
`;

export const Body = styled.div<{ $solid: boolean }>`
  ${typographyCSS("body.sm")}
  color: ${({ theme, $solid }) =>
    $solid ? theme.colors.ui.text.primary : theme.colors.ui.text.secondary};
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space.lg}px;
  margin-top: ${({ theme }) => theme.space["2xs"]}px;
`;

export const ActionLink = styled.button<{ $tone: V3CalloutTone }>`
  ${typographyCSS("label.sm")}
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme, $tone }) => LINK[$tone](theme)};

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme, $tone }) => LINK[$tone](theme)};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;
